import { SignalHookFormState, SignalHookScopeType } from "../types";
import { formatSampleRatePercent, normalizeSampleRate } from "../utils";

import LabelMultiSelect from "./LabelMultiSelect";
import ScopeTypeSelect from "./ScopeTypeSelect";

import { Slider } from "@/components/Slider";
import SelectTemplate from "@/components/SelectTemplate";
import { Entity, EntityType, getEntityNumericId } from "@/queries";

interface HookRowProps {
  hook: SignalHookFormState;
  disableAllRequests?: boolean;
  onChange: (hook: SignalHookFormState) => void;
}

const getTemplateSelectionState = (
  template: Entity<EntityType.PROMPT | EntityType.SNIPPET>,
): Pick<SignalHookFormState, "variantType" | "versionNumber" | "versionLabel"> => {
  if (template.label) {
    return {
      variantType: "label",
      versionNumber: null,
      versionLabel: template.label,
    };
  }

  if (typeof template.version_number === "number") {
    return {
      variantType: "version",
      versionNumber: template.version_number,
      versionLabel: null,
    };
  }

  return {
    variantType: null,
    versionNumber: null,
    versionLabel: null,
  };
};

const HookRow = ({ hook, disableAllRequests = false, onChange }: HookRowProps) => {
  const handleScopeTypeChange = (scopeType: SignalHookFormState["scopeType"]) => {
    onChange({
      ...hook,
      scopeType,
      labelNames: [],
      promptId: undefined,
      promptName: undefined,
      promptFolderId: null,
      versionNumber: null,
      versionLabel: null,
      variantType: null,
    });
  };

  const handleTemplateSelect = (template: Entity<EntityType.PROMPT | EntityType.SNIPPET>) => {
    onChange({
      ...hook,
      promptId: getEntityNumericId(template.id),
      promptName: template.name,
      promptFolderId: template.folder_id ?? null,
      ...getTemplateSelectionState(template),
    });
  };

  const handleLabelNamesChange = (labelNames: string[]) => {
    onChange({ ...hook, labelNames });
  };

  const handleSampleRateChange = (value: string) => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed)) {
      return;
    }

    onChange({
      ...hook,
      sampleRate: normalizeSampleRate(parsed / 100),
    });
  };

  return (
    <div className="space-y-4">
      <ScopeTypeSelect
        value={hook.scopeType}
        disableAllRequests={disableAllRequests}
        onChange={handleScopeTypeChange}
      />

      {hook.scopeType === SignalHookScopeType.PROMPT_LABEL && (
        <LabelMultiSelect selectedLabels={hook.labelNames} onChange={handleLabelNamesChange} />
      )}

      {hook.scopeType === SignalHookScopeType.SPECIFIC_PROMPT && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Prompt template</p>
          <SelectTemplate
            size="xs"
            template={
              hook.promptId
                ? {
                    id: hook.promptId,
                    name: hook.promptName,
                    folder_id: hook.promptFolderId ?? undefined,
                    version_number:
                      hook.variantType === "version"
                        ? (hook.versionNumber ?? undefined)
                        : undefined,
                    label:
                      hook.variantType === "label" ? (hook.versionLabel ?? undefined) : undefined,
                  }
                : undefined
            }
            onSelect={handleTemplateSelect}
            selectLatest={false}
            allowDuplicateVariants
          />
        </div>
      )}

      <div className="space-y-3 pt-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-900">Sampling rate</p>
            <p className="text-sm text-gray-500">
              Randomly evaluate this share of matching requests.
            </p>
          </div>
          <span className="text-sm font-semibold text-gray-900">
            {formatSampleRatePercent(hook.sampleRate)}
          </span>
        </div>
        <Slider
          value={String(Math.round(hook.sampleRate * 100))}
          defaultValue="50"
          min={1}
          max={100}
          step={1}
          onValueChange={handleSampleRateChange}
        />
        <div className="flex justify-between text-xs text-gray-400">
          <span>1%</span>
          <span>100%</span>
        </div>
      </div>
    </div>
  );
};

export default HookRow;
